/**
 * What the whole kitchen has, when you've asked for something specific.
 *
 * Deliberately not `InventoryList` with a filter. That list groups by place and
 * takes the place as given, because it is only ever shown for one open
 * appliance — here place is the *answer*, not the frame, and it has to be on
 * every row. Bending the other component into doing both would have made two
 * confusing layouts out of one clear one.
 */

import { CATEGORY_RULES } from "../features/food/categories";
import { daysUntilExpiry } from "../features/food/expiry";
import { LOCATION_LABELS } from "../features/food/types";
import type { FoodItem } from "../features/food/types";
import { CATEGORY_ICONS, Icon, LOCATION_ICONS } from "./Icon";

import "./SearchResults.css";

interface SearchResultsProps {
  results: FoodItem[];
  query: string;
  today: string;
  onOpen: (item: FoodItem) => void;
}

/** Same three bands the inventory rows use, so a chip means one thing app-wide. */
function urgency(days: number): "now" | "soon" | "later" {
  if (days <= 0) return "now";
  if (days <= 3) return "soon";
  return "later";
}

function expiryLabel(days: number): string {
  if (days < -1) return `${-days} days ago`;
  if (days === -1) return "yesterday";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `${days} days`;
}

export function SearchResults({ results, query, today, onOpen }: SearchResultsProps) {
  if (results.length === 0) {
    return (
      <p className="results__empty">
        Nothing matching “{query.trim()}”. It might be worth adding to the list.
      </p>
    );
  }

  return (
    <>
      <p className="results__count">
        {results.length} {results.length === 1 ? "thing" : "things"}
      </p>

      <ul className="results">
        {results.map((item) => {
          const days = daysUntilExpiry(item, today);
          return (
            <li key={item.id}>
              <button className="results__row" type="button" onClick={() => onOpen(item)}>
                <span className="results__media" data-category={item.category}>
                  {item.photo ? (
                    <>
                      <img className="results__photo" src={item.photo} alt="" />
                      <span className="sr-only">{CATEGORY_RULES[item.category].label}</span>
                    </>
                  ) : (
                    <Icon
                      name={CATEGORY_ICONS[item.category]}
                      className="results__icon"
                      label={CATEGORY_RULES[item.category].label}
                    />
                  )}
                </span>

                <span className="results__text">
                  <span className="results__name">{item.name}</span>
                  <span className="results__meta">
                    {/* Where it is, which is the whole reason to search rather
                        than open three doors in turn. */}
                    <span className="results__place">
                      <Icon
                        name={LOCATION_ICONS[item.location]}
                        className="results__place-icon"
                      />
                      {LOCATION_LABELS[item.location]}
                    </span>
                    <span className="results__amount">
                      {item.quantity} {item.unit}
                    </span>
                  </span>
                </span>

                <span className="results__expiry" data-urgency={urgency(days)}>
                  {days < 0 && <Icon name="dot" className="results__dot" />}
                  {expiryLabel(days)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}
